/**
 * Format complaint SLA / TAT for list and detail views.
 */

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatMinutesAsDuration(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return '—';
  const m = Math.max(0, Math.round(Number(minutes)));
  if (m < 60) return `${m} min`;
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem ? `${h}h ${rem}m` : `${h}h`;
  }
  const d = Math.floor(m / 1440);
  const remH = Math.floor((m % 1440) / 60);
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

export function formatDueInstant(iso) {
  const d = parseDate(iso);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function remainingMs(dueIso, now = new Date()) {
  const due = parseDate(dueIso);
  if (!due) return null;
  return due.getTime() - now.getTime();
}

export function formatRemaining(dueIso, now = new Date()) {
  const ms = remainingMs(dueIso, now);
  if (ms == null) return '—';
  if (ms <= 0) {
    const overdue = Math.abs(ms);
    const mins = Math.ceil(overdue / 60000);
    return `Overdue ${formatMinutesAsDuration(mins)}`;
  }
  const mins = Math.ceil(ms / 60000);
  return `${formatMinutesAsDuration(mins)} left`;
}

function stageState({ done, breached, dueIso, escalatedAt }) {
  if (done) return 'completed';
  if (escalatedAt || breached) return 'breached';
  const ms = remainingMs(dueIso);
  if (ms != null && ms <= 0) return 'overdue';
  return 'pending';
}

function elapsedMinutesBetween(startIso, endIso) {
  const start = parseDate(startIso);
  const end = parseDate(endIso);
  if (!start || !end) return null;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
}

/** Whether a completed stage finished within its SLA window. */
function computeSlaMet(completedAtIso, dueIso, escalatedAt) {
  if (escalatedAt) return false;
  if (!completedAtIso || !dueIso) return null;
  const completed = parseDate(completedAtIso);
  const due = parseDate(dueIso);
  if (!completed || !due) return null;
  return completed.getTime() <= due.getTime();
}

function slaBadgeFromMet(slaMet) {
  if (slaMet === true) {
    return { met: true, text: 'Within SLA' };
  }
  if (slaMet === false) {
    return { met: false, text: 'Out of SLA' };
  }
  return null;
}

/** Policy deadline from complaint creation + configured stage minutes. */
export function getPolicyDueIso(startIso, policyMinutes) {
  const start = parseDate(startIso);
  if (!start || policyMinutes == null) return null;
  return new Date(start.getTime() + Number(policyMinutes) * 60000).toISOString();
}

/**
 * Extra acknowledge window after severity change: policy target already passed
 * but operational due (acknowledge_due_at) is still in the future.
 */
function isAckGraceExtensionActive(policyDueIso, operationalDueIso, escalatedAt) {
  if (escalatedAt) return false;
  const policyDue = parseDate(policyDueIso);
  const operationalDue = parseDate(operationalDueIso);
  const now = new Date();
  if (!policyDue || !operationalDue) return false;
  return (
    now.getTime() > policyDue.getTime() &&
    operationalDue.getTime() > now.getTime()
  );
}

function buildListStageLine({
  done,
  startIso,
  endIso,
  dueIso,
  policyDueIso,
  escalatedAt,
  inProgress,
  graceTitle,
}) {
  const slaDueIso = policyDueIso || dueIso;
  const state = stageState({
    done,
    breached: Boolean(escalatedAt),
    dueIso: slaDueIso,
    escalatedAt,
  });

  if (done && endIso) {
    const elapsed = elapsedMinutesBetween(startIso, endIso);
    const slaMet = computeSlaMet(endIso, slaDueIso, escalatedAt);
    return {
      primary: elapsed != null ? formatMinutesAsDuration(elapsed) : '—',
      slaBadge: slaBadgeFromMet(slaMet),
      state,
      title: elapsed != null
        ? `Completed in ${formatMinutesAsDuration(elapsed)}`
        : undefined,
    };
  }

  if (inProgress) {
    const elapsed = elapsedMinutesBetween(startIso, new Date().toISOString());
    const graceActive =
      policyDueIso &&
      isAckGraceExtensionActive(policyDueIso, dueIso, escalatedAt);

    if (graceActive) {
      const graceRemaining = formatRemaining(dueIso);
      return {
        primary:
          elapsed != null
            ? formatMinutesAsDuration(elapsed)
            : '—',
        graceLine:
          graceRemaining !== '—' && !graceRemaining.startsWith('Overdue')
            ? `Grace: ${graceRemaining}`
            : null,
        slaBadge: slaBadgeFromMet(false),
        graceActive: true,
        state: 'grace',
        title:
          graceTitle ||
          'Past policy acknowledge target; extra time granted after severity change',
      };
    }

    const msLeftPolicy = remainingMs(slaDueIso);
    const policyOnTrack =
      !escalatedAt && msLeftPolicy != null && msLeftPolicy > 0;
    const policyOverdue =
      !escalatedAt && msLeftPolicy != null && msLeftPolicy <= 0;
    const msLeft = remainingMs(dueIso);
    const operationalOverdue =
      !escalatedAt && msLeft != null && msLeft <= 0;
    const primary =
      elapsed != null
        ? formatMinutesAsDuration(elapsed)
        : '—';
    return {
      primary,
      slaBadge: slaBadgeFromMet(
        escalatedAt ? false : policyOverdue || operationalOverdue ? false : policyOnTrack ? true : null,
      ),
      state: escalatedAt || policyOverdue || operationalOverdue ? 'overdue' : state,
      title: 'Elapsed time vs policy SLA target',
    };
  }

  const elapsedFallback = elapsedMinutesBetween(startIso, new Date().toISOString());
  return {
    primary: elapsedFallback != null ? formatMinutesAsDuration(elapsedFallback) : '—',
    slaBadge: null,
    state,
    title: 'Elapsed time since complaint opened',
  };
}

export function getComplaintTatPolicyTooltip(complaint) {
  const ack = complaint?.tat_ack_minutes;
  const close = complaint?.tat_closure_minutes;
  if (ack == null && close == null) return null;
  const sev =
    complaint?.severity != null
      ? String(complaint.severity).charAt(0).toUpperCase() +
        String(complaint.severity).slice(1)
      : 'this severity';
  const lines = [`Applicable TAT (${sev})`];
  if (ack != null) {
    lines.push(`Acknowledge within: ${formatMinutesAsDuration(ack)}`);
  }
  if (close != null) {
    lines.push(`Resolve within: ${formatMinutesAsDuration(close)}`);
  }
  return lines.join('\n');
}

/**
 * Stacked cell for complaints table — elapsed time, SLA pill, policy tooltip.
 */
export function getComplaintTatListSummary(complaint) {
  if (!complaint?.acknowledge_due_at) {
    return { hasTat: false };
  }

  const status = complaint.status;
  const createdAt = complaint.created_at;
  const terminal = status === 'closed' || status === 'cancelled';
  const terminalAt =
    complaint.closed_at || (terminal ? complaint.updated_at : null);

  const ackDone = status !== 'open' || Boolean(complaint.acknowledged_at);
  const ackEnd =
    complaint.acknowledged_at ||
    (ackDone ? complaint.updated_at || complaint.created_at : null);

  const ackPolicyDue = getPolicyDueIso(createdAt, complaint.tat_ack_minutes);

  const ack = buildListStageLine({
    done: ackDone,
    startIso: createdAt,
    endIso: ackEnd,
    dueIso: complaint.acknowledge_due_at,
    policyDueIso: ackPolicyDue,
    escalatedAt: complaint.ack_escalated_at,
    inProgress: !ackDone,
    graceTitle:
      'Past policy acknowledge target; extra time granted after severity change',
  });

  const closureDone = terminal && Boolean(terminalAt);
  const closurePolicyDue = getPolicyDueIso(createdAt, complaint.tat_closure_minutes);
  const closure = buildListStageLine({
    done: closureDone,
    startIso: createdAt,
    endIso: terminalAt,
    dueIso: complaint.closure_target_at,
    policyDueIso: closurePolicyDue,
    escalatedAt: complaint.closure_escalated_at,
    inProgress: !terminal,
  });

  return {
    hasTat: true,
    policyTooltip: getComplaintTatPolicyTooltip(complaint),
    closureStageLabel: status === 'cancelled' ? 'Cancel' : 'Close',
    ack,
    closure,
  };
}

/** Shown when ack_escalated_at / closure_escalated_at is set (breach recorded, not per-channel delivery). */
export const TAT_ESCALATION_NOTIFY_LINE =
  'System notified assigned technician and location managers.';

/**
 * Detail panel rows for ComplaintView (elapsed time + within/out of SLA).
 */
export function getComplaintTatDetailRows(complaint) {
  if (!complaint?.acknowledge_due_at) return [];

  const summary = getComplaintTatListSummary(complaint);
  if (!summary.hasTat) return [];

  const terminal = complaint.status === 'closed' || complaint.status === 'cancelled';
  const terminalAt =
    complaint.closed_at || (terminal ? complaint.updated_at : null);
  const ackCompletedAt = complaint.acknowledged_at
    ? formatDueInstant(complaint.acknowledged_at)
    : null;
  const closureCompletedAt = terminalAt ? formatDueInstant(terminalAt) : null;

  const closureTitle =
    complaint.status === 'cancelled' ? 'Cancellation SLA' : 'Resolution SLA';

  const ackPolicyDue = getPolicyDueIso(
    complaint.created_at,
    complaint.tat_ack_minutes,
  );

  return [
    {
      key: 'ack',
      title: 'Acknowledgment SLA',
      target: formatDueInstant(complaint.acknowledge_due_at),
      targetHint: summary.ack.graceActive
        ? 'Extended deadline after severity change'
        : null,
      policyTarget: ackPolicyDue ? formatDueInstant(ackPolicyDue) : null,
      duration: summary.ack.primary,
      graceLine: summary.ack.graceLine,
      slaBadge: summary.ack.slaBadge,
      state: summary.ack.state,
      policyMinutes: complaint.tat_ack_minutes,
      completedAt: ackCompletedAt,
      escalatedAt: complaint.ack_escalated_at || null,
    },
    {
      key: 'closure',
      title: closureTitle,
      target: formatDueInstant(complaint.closure_target_at),
      duration: summary.closure.primary,
      slaBadge: summary.closure.slaBadge,
      state: summary.closure.state,
      policyMinutes: complaint.tat_closure_minutes,
      completedAt: closureCompletedAt,
      escalatedAt: complaint.closure_escalated_at || null,
    },
  ];
}

export function formatPolicyMinutesRow(version) {
  if (!version) return [];
  return [
    {
      severity: 'Critical',
      ack: formatMinutesAsDuration(version.critical_ack_minutes),
      closure: formatMinutesAsDuration(version.critical_closure_minutes),
    },
    {
      severity: 'Major',
      ack: formatMinutesAsDuration(version.major_ack_minutes),
      closure: formatMinutesAsDuration(version.major_closure_minutes),
    },
    {
      severity: 'Minor',
      ack: formatMinutesAsDuration(version.minor_ack_minutes),
      closure: formatMinutesAsDuration(version.minor_closure_minutes),
    },
  ];
}

const TAT_FIELD_LABELS = {
  effective_from: 'Effective from',
  critical_ack_minutes: 'Critical — acknowledge within',
  critical_closure_minutes: 'Critical — close within',
  major_ack_minutes: 'Major — acknowledge within',
  major_closure_minutes: 'Major — close within',
  minor_ack_minutes: 'Minor — acknowledge within',
  minor_closure_minutes: 'Minor — close within',
  note: 'Note',
};

export function formatTatAuditFieldLabel(fieldName) {
  return TAT_FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ');
}

export function formatTatAuditFieldValue(fieldName, raw) {
  if (raw == null || raw === '') return '—';
  if (fieldName === 'effective_from') return formatDueInstant(raw);
  if (fieldName.endsWith('_minutes')) return formatMinutesAsDuration(raw);
  return String(raw);
}

/** Rows comparing scheduled policy to current (for scheduled-change panel). */
export function formatPolicyDiffRows(current, scheduled) {
  const rows = formatPolicyMinutesRow(scheduled);
  if (!current) return rows.map((r) => ({ ...r, changed: true }));
  const keys = [
    ['critical', 'critical_ack_minutes', 'critical_closure_minutes'],
    ['major', 'major_ack_minutes', 'major_closure_minutes'],
    ['minor', 'minor_ack_minutes', 'minor_closure_minutes'],
  ];
  return rows.map((row, i) => {
    const [, ackKey, cloKey] = keys[i];
    const ackChanged =
      Number(scheduled[ackKey]) !== Number(current[ackKey]);
    const cloChanged =
      Number(scheduled[cloKey]) !== Number(current[cloKey]);
    const cur = formatPolicyMinutesRow(current)[i];
    return {
      ...row,
      changed: ackChanged || cloChanged,
      currentAck: cur.ack,
      currentClosure: cur.closure,
    };
  });
}

export function formatTatAuditActionLabel(actionType) {
  switch (actionType) {
    case 'CREATE':
      return 'Policy version created';
    case 'UPDATE':
      return 'Scheduled change updated';
    case 'DELETE':
      return 'Scheduled change cancelled';
    default:
      return actionType;
  }
}

/** Short action label for change-history table. */
export function formatTatAuditActionShort(actionType) {
  switch (actionType) {
    case 'CREATE':
      return 'Created';
    case 'UPDATE':
      return 'Updated';
    case 'DELETE':
      return 'Cancelled';
    default:
      return actionType || '—';
  }
}

const TAT_AUDIT_SHORT_LABELS = {
  effective_from: 'Effective',
  critical_ack_minutes: 'Critical ack',
  critical_closure_minutes: 'Critical close',
  major_ack_minutes: 'Major ack',
  major_closure_minutes: 'Major close',
  minor_ack_minutes: 'Minor ack',
  minor_closure_minutes: 'Minor close',
  note: 'Note',
};

const TAT_AUDIT_HIDDEN_FIELDS = new Set(['location_id', 'organization_id']);

export function getTatAuditVisibleChanges(changes = []) {
  return (changes || []).filter((c) => !TAT_AUDIT_HIDDEN_FIELDS.has(c.field_name));
}

function tatAuditShortFieldLabel(fieldName) {
  return TAT_AUDIT_SHORT_LABELS[fieldName] || fieldName.replace(/_/g, ' ');
}

const TAT_SEVERITY_FIELD_GROUPS = {
  critical: ['critical_ack_minutes', 'critical_closure_minutes'],
  major: ['major_ack_minutes', 'major_closure_minutes'],
  minor: ['minor_ack_minutes', 'minor_closure_minutes'],
};

const TAT_AUDIT_FIELD_ORDER = [
  'effective_from',
  'critical_ack_minutes',
  'critical_closure_minutes',
  'major_ack_minutes',
  'major_closure_minutes',
  'minor_ack_minutes',
  'minor_closure_minutes',
  'note',
];

function categorizeTatAuditChanges(changes) {
  const fields = new Set(changes.map((c) => c.field_name));
  return {
    schedule: fields.has('effective_from'),
    note: fields.has('note'),
    critical: TAT_SEVERITY_FIELD_GROUPS.critical.some((f) => fields.has(f)),
    major: TAT_SEVERITY_FIELD_GROUPS.major.some((f) => fields.has(f)),
    minor: TAT_SEVERITY_FIELD_GROUPS.minor.some((f) => fields.has(f)),
  };
}

function severityChangeCount(cat) {
  return [cat.critical, cat.major, cat.minor].filter(Boolean).length;
}

function changedSeverityNames(cat) {
  const names = [];
  if (cat.critical) names.push('Critical');
  if (cat.major) names.push('Major');
  if (cat.minor) names.push('Minor');
  return names;
}

function effectiveFromSnippet(changes, preferNew = true) {
  const ch = changes.find((c) => c.field_name === 'effective_from');
  if (!ch) return null;
  const raw = preferNew ? ch.new_value ?? ch.old_value : ch.old_value ?? ch.new_value;
  if (raw == null || raw === '') return null;
  return formatTatAuditFieldValue('effective_from', raw);
}

/** Semantic one-line summary for change-history (details hold field-level data). */
export function buildTatAuditSummary(entry) {
  const actionType = entry?.action_type;
  const changes = getTatAuditVisibleChanges(entry?.changes);

  if (!changes.length) {
    return '—';
  }

  if (actionType === 'DELETE') {
    const effStr = effectiveFromSnippet(changes, false);
    return effStr
      ? `Cancelled scheduled change (was effective ${effStr})`
      : 'Cancelled scheduled change';
  }

  const cat = categorizeTatAuditChanges(changes);
  const sevCount = severityChangeCount(cat);
  const effNew = effectiveFromSnippet(changes, true);

  if (actionType === 'CREATE') {
    if (effNew && sevCount === 3) {
      return `Full policy scheduled · effective ${effNew}`;
    }
    if (effNew) {
      const sevLabel =
        sevCount === 0
          ? 'Policy version'
          : sevCount === 1
            ? `${changedSeverityNames(cat)[0]} SLA`
            : `${changedSeverityNames(cat).join(' & ')} SLA`;
      return `${sevLabel} scheduled · effective ${effNew}`;
    }
    if (sevCount === 3) {
      return 'Initial complaint TAT policy configured';
    }
    return 'New complaint TAT policy created';
  }

  // UPDATE
  const onlySchedule = cat.schedule && sevCount === 0 && !cat.note;
  const onlyNote = cat.note && sevCount === 0 && !cat.schedule;
  const onlySla = sevCount > 0 && !cat.schedule && !cat.note;

  if (onlySchedule) {
    return effNew
      ? `Scheduled effective time changed to ${effNew}`
      : 'Scheduled effective time changed';
  }

  if (onlyNote) {
    return 'Note updated';
  }

  if (onlySla && sevCount === 3) {
    return 'All severity SLA targets updated';
  }

  if (onlySla && sevCount === 1) {
    return `${changedSeverityNames(cat)[0]} SLA targets updated`;
  }

  if (onlySla && sevCount === 2) {
    return `${changedSeverityNames(cat).join(' & ')} SLA targets updated`;
  }

  const segments = [];

  if (cat.schedule) {
    segments.push(
      effNew ? `Schedule moved to ${effNew}` : 'Schedule time changed',
    );
  }

  if (sevCount === 3) {
    segments.push('all SLA targets updated');
  } else if (sevCount === 2) {
    segments.push(`${changedSeverityNames(cat).join(' & ')} SLA updated`);
  } else if (sevCount === 1) {
    segments.push(`${changedSeverityNames(cat)[0]} SLA updated`);
  }

  if (cat.note) {
    segments.push('note updated');
  }

  if (!segments.length) {
    return `${changes.length} field${changes.length === 1 ? '' : 's'} updated`;
  }

  const joined =
    segments.length === 1
      ? segments[0].charAt(0).toUpperCase() + segments[0].slice(1)
      : segments
          .map((s, i) => (i === 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s))
          .join('; ');

  return joined;
}

function sortTatAuditChanges(changes) {
  const order = new Map(TAT_AUDIT_FIELD_ORDER.map((f, i) => [f, i]));
  return [...changes].sort(
    (a, b) => (order.get(a.field_name) ?? 99) - (order.get(b.field_name) ?? 99),
  );
}

/** True when CREATE was scheduled against an existing in-effect policy (baseline in audit). */
export function isTatAuditScheduledAgainstCurrent(entry) {
  if (entry?.action_type !== 'CREATE') return false;
  return getTatAuditVisibleChanges(entry?.changes).some(
    (c) => c.old_value != null && c.old_value !== '',
  );
}

/** Rows for expanded Before | After detail table. */
export function getTatAuditDetailTableRows(entry) {
  const actionType = entry?.action_type;
  const changes = sortTatAuditChanges(getTatAuditVisibleChanges(entry?.changes));

  return changes.map((change) => {
    const field = tatAuditShortFieldLabel(change.field_name);
    const fmt = (raw) => formatTatAuditFieldValue(change.field_name, raw);

    if (actionType === 'DELETE') {
      return {
        field,
        before: fmt(change.old_value),
        after: '—',
      };
    }

    const oldV = change.old_value;
    const newV = change.new_value;
    const hasOld = oldV != null && oldV !== '';
    const hasNew = newV != null && newV !== '';

    // CREATE with baseline: old_value is the policy in effect at schedule time
    if (actionType === 'CREATE') {
      return {
        field,
        before: hasOld ? fmt(oldV) : '—',
        after: hasNew ? fmt(newV) : '—',
      };
    }

    if (!hasOld) {
      return {
        field,
        before: '—',
        after: fmt(newV ?? oldV),
      };
    }

    return {
      field,
      before: fmt(oldV),
      after: hasNew ? fmt(newV) : '—',
    };
  });
}

/** Minimum lead time before a scheduled TAT version may take effect. */
export const TAT_SCHEDULE_MIN_LEAD_MINUTES = 30;

export const TAT_POLICY_MINUTE_FIELDS = [
  'critical_ack_minutes',
  'critical_closure_minutes',
  'major_ack_minutes',
  'major_closure_minutes',
  'minor_ack_minutes',
  'minor_closure_minutes',
];

const TAT_MINUTE_FIELD_LABELS = {
  critical_ack_minutes: 'Critical acknowledgment (minutes)',
  critical_closure_minutes: 'Critical resolution (minutes)',
  major_ack_minutes: 'Major acknowledgment (minutes)',
  major_closure_minutes: 'Major resolution (minutes)',
  minor_ack_minutes: 'Minor acknowledgment (minutes)',
  minor_closure_minutes: 'Minor resolution (minutes)',
};

/** Keep only non-negative integer digits while the user types. */
export function sanitizeTatMinutesInput(value) {
  if (value === '' || value == null) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits === '') return '';
  return String(parseInt(digits, 10));
}

/**
 * @returns {string|null} Error message, or null if all minute fields are valid.
 */
export function validateTatPolicyMinutes(form) {
  for (const field of TAT_POLICY_MINUTE_FIELDS) {
    const raw = form[field];
    const label = TAT_MINUTE_FIELD_LABELS[field] || field;
    if (raw === '' || raw == null) {
      return `${label} is required.`;
    }
    const text = String(raw).trim();
    if (!/^\d+$/.test(text)) {
      return `${label} must be a whole number.`;
    }
    const n = Number(text);
    if (!Number.isInteger(n) || n < 1) {
      return `${label} must be at least 1 minute.`;
    }
  }
  return null;
}

/** Parsed minute values for API payload. */
export function parseTatPolicyMinutesFromForm(form) {
  const out = {};
  for (const field of TAT_POLICY_MINUTE_FIELDS) {
    out[field] = parseInt(String(form[field]).trim(), 10);
  }
  return out;
}

/** `datetime-local` value (local wall clock, no timezone) from a Date. */
export function toDatetimeLocalValue(date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/** Parse `datetime-local` as local wall time (avoids UTC mis-parse of "YYYY-MM-DDTHH:mm"). */
export function parseDatetimeLocalToDate(localValue) {
  if (!localValue || typeof localValue !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localValue.trim());
  if (!match) return null;
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const h = Number(match[4]);
  const mi = Number(match[5]);
  const date = new Date(y, mo - 1, d, h, mi, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** ISO-8601 UTC string for API from a `datetime-local` value. */
export function datetimeLocalToIso(localValue) {
  const date = parseDatetimeLocalToDate(localValue);
  return date ? date.toISOString() : null;
}

export function defaultScheduleEffectiveLocal(
  leadMinutes = TAT_SCHEDULE_MIN_LEAD_MINUTES,
) {
  const d = new Date();
  d.setMinutes(d.getMinutes() + leadMinutes);
  return toDatetimeLocalValue(d);
}

export function minScheduleDatetimeLocal(leadMinutes = TAT_SCHEDULE_MIN_LEAD_MINUTES) {
  return defaultScheduleEffectiveLocal(leadMinutes);
}

/**
 * @returns {string|null} Error message, or null if valid for a future schedule.
 */
export function validateScheduleEffectiveLocal(
  localValue,
  leadMinutes = TAT_SCHEDULE_MIN_LEAD_MINUTES,
) {
  const picked = parseDatetimeLocalToDate(localValue);
  if (!picked) return 'Effective date and time are required.';
  const min = new Date();
  min.setMinutes(min.getMinutes() + leadMinutes);
  if (picked.getTime() < min.getTime()) {
    return `Choose a time at least ${leadMinutes} minutes from now (same day is allowed).`;
  }
  return null;
}
